"use client";

import { useEffect, useMemo, useRef } from "react";
import { midiToNoteName, type MidiNote, type PitchFrame, type TrackRole } from "../lib/music";

type TimelineChord = { bar: number; label: string; warning: boolean };
type TimelineTransition = { atBeat: number; label: string };
type TimelineTrack = { id: string | number; name: string; role: TrackRole; notes: MidiNote[]; enabled: boolean; color: string };

type Props = {
  frames: PitchFrame[];
  notes: MidiNote[];
  bpm: number;
  duration: number;
  confidenceThreshold: number;
  chords?: TimelineChord[];
  transitions?: TimelineTransition[];
  tracks?: TimelineTrack[];
  horizontalZoom?: number;
  playheadBeat?: number | null;
  selectedBar?: number | null;
  selectedBarCount?: number;
  onSelectBar?: (bar: number) => void;
  selectedNoteIndex?: number | null;
  onSelectNote?: (index: number) => void;
};

const BLACK_KEYS = new Set([1, 3, 6, 8, 10]);

export function PitchRoll({ frames, notes, bpm, duration, confidenceThreshold, chords = [], transitions = [], tracks = [], horizontalZoom = 1, playheadBeat = null, selectedBar = null, selectedBarCount = 1, onSelectBar, selectedNoteIndex = null, onSelectNote }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const totalBeats = useMemo(() => Math.max(4, duration * bpm / 60, ...notes.map((note) => note.startBeat + note.durationBeats), ...tracks.flatMap((track) => track.notes.map((note) => note.startBeat + note.durationBeats)), chords.length * 4), [bpm, chords.length, duration, notes, tracks]);
  const visibleTracks = tracks.filter((track) => track.enabled);
  const canvasHeight = 318 + visibleTracks.length * 42;
  const barCount = Math.max(1, Math.ceil(totalBeats / 4));
  const minimumTimelineWidth = barCount * 88;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.scale(ratio, ratio);
    context.clearRect(0, 0, width, height);

    const labelWidth = 58;
    const chordHeight = 48;
    const pitchTop = chordHeight;
    const pitchHeight = 260;
    const plotWidth = width - labelWidth;
    const detected = [...frames.filter((frame) => frame.midiFloat !== null && frame.confidence >= (frame.acceptanceConfidence ?? confidenceThreshold)).map((frame) => frame.midiFloat as number), ...notes.map((note) => note.midi)];
    const detectedMin = detected.length ? Math.min(...detected) : 55;
    const detectedMax = detected.length ? Math.max(...detected) : 67;
    let minMidi = Math.max(24, Math.floor(detectedMin - 2));
    let maxMidi = Math.min(108, Math.ceil(detectedMax + 2));
    if (maxMidi - minMidi < 12) {
      const extra = 12 - (maxMidi - minMidi);
      minMidi = Math.max(24, minMidi - Math.ceil(extra / 2));
      maxMidi = Math.min(108, maxMidi + Math.floor(extra / 2));
    }
    const span = Math.max(1, maxMidi - minMidi + 1);
    const xForBeat = (beat: number) => labelWidth + (beat / totalBeats) * plotWidth;
    const yFor = (midi: number) => pitchTop + ((maxMidi - midi + 0.5) / span) * pitchHeight;

    context.fillStyle = "#101318";
    context.fillRect(0, 0, width, height);
    context.fillStyle = "#171c20";
    context.fillRect(labelWidth, 0, plotWidth, chordHeight);

    if (selectedBar !== null) {
      context.fillStyle = "rgba(183,128,255,.12)";
      context.fillRect(xForBeat(selectedBar * 4), 0, xForBeat((selectedBar + selectedBarCount) * 4) - xForBeat(selectedBar * 4), height);
    }

    chords.forEach((chord) => {
      const startX = xForBeat(chord.bar * 4);
      const endX = xForBeat((chord.bar + 1) * 4);
      context.fillStyle = chord.warning ? "rgba(255,154,77,.1)" : "rgba(34,216,111,.08)";
      context.fillRect(startX + 1, 2, Math.max(1, endX - startX - 2), chordHeight - 4);
      context.fillStyle = chord.warning ? "#ffb37a" : "#8bf0ae";
      context.font = "800 14px ui-monospace, monospace";
      context.fillText(chord.label, startX + 8, 24);
      context.fillStyle = "#77827c";
      context.font = "8px ui-monospace, monospace";
      context.fillText(`BAR ${chord.bar + 1}`, startX + 8, 38);
    });

    transitions.forEach((transition) => {
      const x = xForBeat(transition.atBeat);
      context.fillStyle = "#b780ff";
      context.beginPath();
      context.moveTo(x, 6);
      context.lineTo(x + 5, 11);
      context.lineTo(x, 16);
      context.lineTo(x - 5, 11);
      context.closePath();
      context.fill();
      context.font = "800 8px ui-monospace, monospace";
      context.fillText(transition.label, x + 7, 14);
    });

    for (let midi = minMidi; midi <= maxMidi; midi += 1) {
      const y = yFor(midi);
      const pitchClass = ((midi % 12) + 12) % 12;
      context.fillStyle = pitchClass === 0 ? "#191d23" : BLACK_KEYS.has(pitchClass) ? "#0d1014" : "#14181d";
      context.fillRect(labelWidth, y, plotWidth, pitchHeight / span + 1);
      context.strokeStyle = pitchClass === 0 ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.045)";
      context.beginPath(); context.moveTo(labelWidth, y); context.lineTo(width, y); context.stroke();
      if (pitchClass === 0 || midi === minMidi || midi === maxMidi) {
        context.fillStyle = "#8d949e"; context.font = "9px ui-monospace, monospace"; context.fillText(midiToNoteName(midi), 8, y + 9);
      }
    }

    const gridBeat = totalBeats > 64 ? 4 : totalBeats > 32 ? 2 : 1;
    for (let beat = 0; beat <= totalBeats; beat += gridBeat) {
      const x = xForBeat(beat);
      context.strokeStyle = beat % 4 === 0 ? "rgba(255,255,255,.22)" : "rgba(255,255,255,.08)";
      context.beginPath(); context.moveTo(x, chordHeight); context.lineTo(x, height); context.stroke();
    }

    notes.forEach((note, index) => {
      const y = yFor(note.midi);
      const startX = xForBeat(note.startBeat);
      const endX = xForBeat(note.startBeat + note.durationBeats);
      const active = playheadBeat !== null && playheadBeat >= note.startBeat && playheadBeat < note.startBeat + note.durationBeats;
      context.fillStyle = index === selectedNoteIndex ? "#c59aff" : active ? "#d9ffe6" : "rgba(28,211,108,.78)";
      context.shadowColor = active ? "#8dffb5" : "transparent";
      context.shadowBlur = active ? 12 : 0;
      context.fillRect(startX, y - 4, Math.max(3, endX - startX - 1), 8);
      if (index === selectedNoteIndex) {
        context.strokeStyle = "#f0e5ff";
        context.lineWidth = 1.5;
        context.strokeRect(startX - 1, y - 6, Math.max(5, endX - startX + 1), 12);
      }
    });
    context.shadowBlur = 0;

    context.strokeStyle = "#75f6a4";
    context.lineWidth = 1.7;
    context.beginPath();
    let drawing = false;
    let lastValidTime: number | null = null;
    frames.forEach((frame) => {
      const valid = frame.midiFloat !== null && frame.confidence >= (frame.acceptanceConfidence ?? confidenceThreshold) && frame.midiFloat >= minMidi && frame.midiFloat <= maxMidi;
      if (!valid) { if (lastValidTime !== null && frame.time - lastValidTime > 0.18) drawing = false; return; }
      const x = xForBeat(frame.time * bpm / 60);
      const y = yFor(frame.midiFloat as number);
      if (!drawing || lastValidTime === null || frame.time - lastValidTime > 0.18) context.moveTo(x, y); else context.lineTo(x, y);
      drawing = true; lastValidTime = frame.time;
    });
    context.stroke();

    visibleTracks.forEach((track, index) => {
      const laneTop = pitchTop + pitchHeight + index * 42;
      context.fillStyle = index % 2 ? "#11161b" : "#151a20";
      context.fillRect(0, laneTop, width, 42);
      context.fillStyle = track.color;
      context.font = "800 8px ui-monospace, monospace";
      context.fillText(track.name.slice(0, 9), 6, laneTop + 22);
      track.notes.forEach((note) => {
        const startX = xForBeat(note.startBeat);
        const endX = xForBeat(note.startBeat + note.durationBeats);
        const pitchOffset = ((note.midi % 12) / 12) * 15;
        const active = playheadBeat !== null && playheadBeat >= note.startBeat && playheadBeat < note.startBeat + note.durationBeats;
        context.fillStyle = active ? "#ffffff" : track.color;
        context.globalAlpha = active ? 1 : 0.72;
        context.fillRect(startX, laneTop + 29 - pitchOffset, Math.max(3, endX - startX - 1), 5);
        context.globalAlpha = 1;
      });
    });

    if (playheadBeat !== null) {
      const x = xForBeat(playheadBeat);
      context.strokeStyle = "#ffffff"; context.lineWidth = 2; context.shadowColor = "rgba(255,255,255,.8)"; context.shadowBlur = 8;
      context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke(); context.shadowBlur = 0;
    }
  }, [bpm, chords, confidenceThreshold, frames, notes, playheadBeat, selectedBar, selectedBarCount, selectedNoteIndex, totalBeats, transitions, visibleTracks]);

  useEffect(() => {
    const viewport = scrollRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas || playheadBeat === null) return;
    const playheadX = 58 + (playheadBeat / totalBeats) * Math.max(1, canvas.clientWidth - 58);
    const margin = Math.min(96, viewport.clientWidth * 0.24);
    if (playheadX < viewport.scrollLeft + margin || playheadX > viewport.scrollLeft + viewport.clientWidth - margin) {
      viewport.scrollLeft = Math.max(0, playheadX - viewport.clientWidth * 0.38);
    }
  }, [playheadBeat, totalBeats]);

  function selectAtPointer(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!onSelectBar && !onSelectNote) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bounds = canvas.getBoundingClientRect();
    const beat = Math.max(0, Math.min(totalBeats - 0.001, ((event.clientX - bounds.left - 58) / Math.max(1, bounds.width - 58)) * totalBeats));
    const noteIndex = notes.findIndex((note) => beat >= note.startBeat - 0.08 && beat <= note.startBeat + note.durationBeats + 0.08);
    if (noteIndex >= 0) onSelectNote?.(noteIndex);
    onSelectBar?.(Math.floor(beat / 4));
  }

  return <div ref={scrollRef} className="arrange-scroll" aria-label="コード、メロディー、複数トラックを同じ時間軸で見る編集ビュー"><canvas ref={canvasRef} className="pitch-canvas arrange-canvas" style={{ width: `max(${Math.max(1, horizontalZoom) * 100}%, ${minimumTimelineWidth}px)`, height: `${canvasHeight}px` }} onPointerDown={selectAtPointer} /></div>;
}
